// src/lib/forfeoAuthRoles.ts
//
// Adaptateur de compatibilité Lot 4A.
//
// Les composants historiques peuvent continuer d’utiliser les anciens noms,
// mais toutes les normalisations communes passent désormais par le contrat
// canonique Forfeo.

import {
  destinationForForfeoRole,
  normalizeForfeoRole as normalizeCanonicalForfeoRole,
  type ForfeoCanonicalRole,
} from './forfeoCanonicalIdentity';

export type ExplorePortal =
  | 'traveler'
  | 'business'
  | 'habitat'
  | 'forfeopro'
  | 'affiliate'
  | 'prp';

export type HabitatRole = 'tenant' | 'landlord';

export type ForfeoExploreRole =
  | 'customer'
  | 'traveler'
  | 'business'
  | 'merchant'
  | 'partner'
  | 'corporate'
  | 'affiliate'
  | 'prp'
  | 'tenant'
  | 'landlord'
  | 'forfeopro'
  | 'admin'
  | 'support'
  | 'employee'
  | 'sales'
  | string;

const canonicalToLegacyExploreRole = (
  role: ForfeoCanonicalRole,
): ForfeoExploreRole => {
  switch (role) {
    case 'user':
      return 'customer';

    case 'merchant':
      return 'business';

    case 'habitat_landlord':
      return 'landlord';

    case 'habitat_tenant':
      return 'tenant';

    case 'forfeopro':
      return 'forfeopro';

    case 'employee':
      return 'employee';

    default:
      return role;
  }
};

export function normalizeForfeoRole(
  value?: string | null,
  fallback: ForfeoExploreRole = 'customer',
): ForfeoExploreRole {
  const directCanonical = normalizeCanonicalForfeoRole(value);

  if (directCanonical) {
    return canonicalToLegacyExploreRole(directCanonical);
  }

  const raw = String(value || '').trim().toLowerCase();

  // Rôles historiques propres à Explore, non privilégiés par défaut.
  if (
    ['corporate', 'enterprise', 'entreprise'].includes(raw)
  ) {
    return 'corporate';
  }

  if (
    ['sales', 'commercial', 'pulse_sales'].includes(raw)
  ) {
    return 'sales';
  }

  const fallbackCanonical =
    normalizeCanonicalForfeoRole(fallback);

  if (fallbackCanonical) {
    return canonicalToLegacyExploreRole(fallbackCanonical);
  }

  return 'customer';
}

export function selectedPortalToForfeoRole(
  portal: ExplorePortal,
  habitatRole: HabitatRole = 'tenant',
): ForfeoExploreRole {
  if (portal === 'business') return 'business';
  if (portal === 'habitat') return habitatRole;
  if (portal === 'forfeopro') return 'forfeopro';
  if (portal === 'affiliate') return 'affiliate';
  if (portal === 'prp') return 'prp';

  return 'customer';
}

export function routeForExploreRole(
  role?: string | null,
): string {
  const canonical = normalizeCanonicalForfeoRole(role);

  if (canonical) {
    // La route admin historique Explore diffère de la destination commune.
    if (canonical === 'admin') {
      return '/admin-forfeo-portal';
    }

    // Le portail Corporate officiel demeure dans Giftshop.
    // Une authentification Corporate détectée depuis Explore
    // retourne vers le parcours marchand existant plutôt que
    // vers une route Explore inexistante.
    if (canonical === 'corporate') {
      return '/business-dashboard';
    }

    return destinationForForfeoRole(canonical);
  }

  const legacy = normalizeForfeoRole(role);

  if (legacy === 'corporate') {
    return '/business-dashboard';
  }

  if (legacy === 'sales') {
    return '/business-dashboard';
  }

  return '/club';
}

export function labelForForfeoRole(
  role?: string | null,
): string {
  const canonical = normalizeCanonicalForfeoRole(role);

  if (canonical === 'admin') return 'Administrateur';
  if (canonical === 'merchant') return 'Marchand / Partenaire';
  if (canonical === 'corporate') return 'Entreprise';
  if (canonical === 'support') return 'Soutien';
  if (canonical === 'prp') return 'PRP';
  if (canonical === 'affiliate') return 'Ambassadeur';
  if (canonical === 'forfeopro') return 'Forfeo Pros';
  if (canonical === 'habitat_landlord') return 'Propriétaire';
  if (canonical === 'habitat_tenant') return 'Locataire';
  if (canonical === 'employee') return 'Employé';

  return 'Client / Voyageur';
}
